// Fix pdf-parse import issue by using pdfjs-dist directly for browser compatibility
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';

// Set up pdfjs worker for browser environment
if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib?.version}/build/pdf.worker.min.js`;
}

/**
 * Extract text from PDF file using pdfjs-dist directly
 * @param {File} file - PDF file object
 * @returns {Promise<string>} Extracted text content
 */
export async function extractTextFromPDF(file) {
  try {
    const arrayBuffer = await file?.arrayBuffer();
    const pdf = await pdfjsLib?.getDocument({ data: arrayBuffer })?.promise;
    
    let fullText = '';
    
    // Extract text from all pages
    for (let pageNum = 1; pageNum <= pdf?.numPages; pageNum++) {
      const page = await pdf?.getPage(pageNum);
      const textContent = await page?.getTextContent();
      const pageText = textContent?.items?.map(item => item?.str)?.join(' ') || '';
      fullText += pageText + '\n';
    }
    
    return fullText?.trim();
  } catch (error) {
    console.error('Error extracting text from PDF:', error);
    throw new Error('Failed to extract text from PDF: ' + error.message);
  }
}

/**
 * Extract text from DOC/DOCX file
 * @param {File} file - DOC or DOCX file object
 * @returns {Promise<string>} Extracted text content
 */
export async function extractTextFromDOC(file) {
  try {
    const arrayBuffer = await file?.arrayBuffer();
    const result = await mammoth?.extractRawText({ arrayBuffer });
    return result?.value;
  } catch (error) {
    console.error('Error extracting text from DOC:', error);
    throw new Error('Failed to extract text from DOC: ' + error.message);
  }
}

/**
 * Extract text from any supported document type
 * @param {File} file - Document file (PDF, DOC, DOCX)
 * @returns {Promise<Object>} Object containing text and metadata
 */
export async function extractTextFromDocument(file) {
  const fileExtension = file?.name?.split('.')?.pop()?.toLowerCase();
  
  try {
    let extractedText = '';
    
    switch (fileExtension) {
      case 'pdf':
        extractedText = await extractTextFromPDF(file);
        break;
      case 'doc':
      case 'docx':
        extractedText = await extractTextFromDOC(file);
        break;
      default:
        throw new Error(`Unsupported file type: ${fileExtension}`);
    }

    return {
      text: extractedText,
      metadata: {
        fileName: file?.name,
        fileSize: file?.size,
        fileType: fileExtension,
        extractedAt: new Date()?.toISOString(),
        wordCount: extractedText?.split(/\s+/)?.length,
        characterCount: extractedText?.length
      }
    };
  } catch (error) {
    console.error('Document parsing error:', error);
    throw error;
  }
}

/**
 * Validate document file before parsing
 * @param {File} file - Document file to validate
 * @returns {Object} Validation result
 */
export function validateDocument(file) {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = ['pdf', 'doc', 'docx'];
  const fileExtension = file?.name?.split('.')?.pop()?.toLowerCase();

  const validation = {
    isValid: true,
    errors: []
  };

  if (!file) {
    validation.isValid = false;
    validation?.errors?.push('No file provided');
    return validation;
  }

  if (file?.size > maxSize) {
    validation.isValid = false;
    validation?.errors?.push(`File size exceeds 10MB limit (${(file?.size / 1024 / 1024)?.toFixed(2)}MB)`);
  }

  if (!allowedTypes?.includes(fileExtension)) {
    validation.isValid = false;
    validation?.errors?.push(`Unsupported file type: ${fileExtension}. Allowed types: ${allowedTypes?.join(', ')}`);
  }

  if (!file?.name || file?.name?.trim() === '') {
    validation.isValid = false;
    validation?.errors?.push('Invalid file name');
  }

  return validation;
}

/**
 * Get file type from file extension
 * @param {string} fileName - Name of the file
 * @returns {string} File type (pdf, doc, docx)
 */
export function getDocumentType(fileName) {
  const extension = fileName?.split('.')?.pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return 'pdf';
    case 'doc':
      return 'doc';
    case 'docx':
      return 'docx';
    default:
      return 'unknown';
  }
}

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
}

/**
 * Parse document - main export function for backward compatibility
 * @param {File} file - Document file to parse
 * @returns {Promise<Object>} Parsed document result
 */
export async function parseDocument(file) {
  const validation = validateDocument(file);
  
  if (!validation?.isValid) {
    throw new Error(`Invalid document: ${validation?.errors?.join(', ')}`);
  }
  
  return await extractTextFromDocument(file);
}

// Export parseDocument as default for backward compatibility
export default parseDocument;